import React, { useState, useEffect, useId } from "react";
import cn from "classnames";
import { Link } from "react-router-dom";

import SelectInput from "../selectInput";
import styles from "./pagination.module.css";

const sortList = [
  { value: "10", label: "10" },
  { value: "20", label: "20" },
  { value: "50", label: "50" },
  { value: "100", label: "100" }
];

export const Pagination = ({ className, links, currentPage, lastPage, perPage, handlePerPage, isStartFromFirst, defaultSize }) => {
  const perPageLabelId = useId();
  const [valuePerPage, setValuePerPage] = useState({ value: "", label: "10" });
  // Every size this pager has been given. The size shown is the one the server answered with, and that need not be one
  // of the fixed sizes (json-api answers 25 by default). The menu keeps offering such a size after another one is chosen,
  // so the member can always return to the size they started on (GROWTH-6668).
  const [seenSizes, setSeenSizes] = useState([]);
  const currentSize = perPage ? String(perPage) : "";

  const currentPageLink = links.find(({ number }) => {
    return number === currentPage;
  });

  const renderPageLinks = () => {
    let startPage;
    let endPage;
    const totalPages = links.length;

    if (totalPages <= 10) {
      startPage = 0;
      endPage = totalPages;
    } else {
      // eslint-disable-next-line no-lonely-if
      if (currentPage <= 6) {
        startPage = 0;
        endPage = 10;
      } else if (currentPage + 4 >= totalPages) {
        startPage = totalPages - 9;
        endPage = totalPages;
      } else {
        startPage = currentPage - 5;
        endPage = currentPage + 4;
      }
    }

    const surroundLinks = links.slice(startPage, endPage);

    return (
      <div className={styles.linkRowWrp}>
        {surroundLinks.map(({ pathname, search, number }) => {
          return (
            <Link
              to={{ pathname, search }}
              key={search + number}
              className={cn(styles.paginationItem, currentPage === number ? styles.paginationItemActive : "")}
            >
              {isStartFromFirst ? number : number + 1}
            </Link>
          );
        })}
      </div>
    );
  };

  const renderArrowLinks = isPrev => {
    const isPrevDisabled = currentPage === 0 || (isStartFromFirst && currentPage === 1);
    const isNextDisabled = !currentPageLink || currentPageLink.number === lastPage;
    const targetNumber = isPrev ? currentPage - 1 : currentPage + 1;
    // The neighbour's own link carries every query param exactly as the caller built it. Rewriting the first
    // "page=" in the current search hit the wrong param once a page held two pagers (?page=…&sub_page=…).
    // The rewrite stays as the fallback for a links list that does not contain the neighbour (GROWTH-6643).
    const targetLink = links.find(({ number }) => number === targetNumber);
    const targetPathname = targetLink ? targetLink.pathname : currentPageLink?.pathname;
    const newParams = targetLink ? targetLink.search : currentPageLink?.search?.replace(/(page=)[^&]+/, `page=${targetNumber}`);

    if (isPrev) {
      if (isPrevDisabled) {
        return <span className={cn(styles.linkWrp, styles.linkWrpDisable)}>Prev</span>;
      }
      return (
        <Link to={{ pathname: targetPathname, search: newParams }} className={styles.linkWrp}>
          Prev
        </Link>
      );
    }

    if (isNextDisabled) {
      return <span className={cn(styles.linkWrp, styles.linkWrpDisable)}>Next</span>;
    }
    return (
      <Link to={{ pathname: targetPathname, search: newParams }} className={styles.linkWrp}>
        Next
      </Link>
    );
  };

  useEffect(() => {
    if (String(valuePerPage.value) !== String(perPage)) {
      setValuePerPage({ value: perPage, label: perPage });
    }
  }, [perPage]);

  useEffect(() => {
    if (currentSize && !seenSizes.includes(currentSize)) {
      setSeenSizes(previous => [...previous, currentSize]);
    }
  }, [currentSize]); // eslint-disable-line

  // The fixed sizes plus any other size seen, in numeric order. A size already in the list adds nothing, so a consumer
  // whose server answers 10, 20, 50 or 100 gets the same menu as before. A consumer that names its list's default
  // (`defaultSize`) always has it in the menu: a list opened at another size (reload, Back, shared link) has never seen the
  // server answer with it (GROWTH-7502).
  const defaultSizeText = defaultSize ? String(defaultSize) : "";
  const extraSizes = [...new Set([...seenSizes, currentSize, defaultSizeText])].filter(
    size => size && !sortList.some(({ value }) => value === size)
  );
  const perPageOptions =
    extraSizes.length === 0
      ? sortList
      : [...sortList, ...extraSizes.map(size => ({ value: size, label: size }))].sort((a, b) => Number(a.value) - Number(b.value));

  return (
    <nav className={cn(styles.pagination, className)}>
      {perPage && (
        <div className={styles.perPageContent}>
          <span id={perPageLabelId} className={styles.perPageText}>
            Items per page
          </span>
          <SelectInput
            tabSelectsValue={false}
            className={styles.select}
            ariaLabelledBy={perPageLabelId}
            name="perPage"
            placeholder="10"
            onChange={item => {
              handlePerPage(item.value);
              setValuePerPage(item);
            }}
            options={perPageOptions}
            value={valuePerPage}
          />
        </div>
      )}

      {renderArrowLinks(true)}
      {renderPageLinks()}
      {renderArrowLinks()}
    </nav>
  );
};
